import { useState } from "react";
import { Download, ExternalLink, FolderOpen } from "lucide-react";
import { formatMessageTime } from "../lib/utils";
import {
  FileText,
  FileImage,
  File as FileGeneric,
  Music,
  Video,
} from "lucide-react";
import toast from "react-hot-toast";

interface FileItem {
  messageId: string;
  fileName: string;
  fileSize: number;
  fileUrl: string;
  type: string;
  senderName: string;
  timestamp: string;
}

interface FileHistorySidebarProps {
  files: FileItem[];
}

const FileHistorySidebar = ({ files }: FileHistorySidebarProps) => {
  const [filterType, setFilterType] = useState<string>("all");

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
      return <FileImage size={20} className="text-[#00AAF1]" />;
    if (["pdf", "doc", "docx", "txt", "xls", "xlsx", "html", "htm"].includes(ext))
      return <FileText size={20} className="text-[#03488A]" />;
    if (["mp4", "avi", "mov", "mkv"].includes(ext))
      return <Video size={20} className="text-sky-500" />;
    if (["mp3", "wav", "aac", "webm"].includes(ext))
      return <Music size={20} className="text-violet-500" />;
    return <FileGeneric size={20} className="text-slate-500" />;
  };

  const handleViewFile = async (url: string, fileName: string) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.target = "_blank";
      link.click();
    } catch {
      toast.error("Failed to open file");
    }
  };

  const filteredFiles =
    filterType === "all" ? files : files.filter((f) => f.type === filterType);

  const fileTypes = [
    { id: "all", label: "All", count: files.length },
    {
      id: "image",
      label: "Images",
      count: files.filter((f) => f.type === "image").length,
    },
    {
      id: "document",
      label: "Docs",
      count: files.filter((f) => f.type === "document").length,
    },
    {
      id: "video",
      label: "Video",
      count: files.filter((f) => f.type === "video").length,
    },
    {
      id: "audio",
      label: "Audio",
      count: files.filter((f) => f.type === "audio").length,
    },
  ];

  const brandGradient = "bg-[linear-gradient(135deg,#03488A_0%,#00AAF1_100%)]";

  return (
    <div className="w-80 bg-white border-l border-slate-200/60 flex flex-col overflow-hidden">

      <div className="px-5 pt-5 pb-4 border-b border-slate-200/60">
        <div className="flex items-center gap-2.5 mb-3">
          <div
            className={`w-9 h-9 rounded-xl ${brandGradient} grid place-items-center shadow-sm shadow-[#00AAF1]/20`}
          >
            <FolderOpen className="w-4.5 h-4.5 text-white" size={18} />
          </div>
          <div className="leading-tight">
            <h2 className="text-sm font-extrabold text-slate-800 tracking-tight">
              File History
            </h2>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
              {files.length} {files.length === 1 ? "file" : "files"} shared
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {fileTypes.map((type) => {
            const active = filterType === type.id;
            return (
              <button
                key={type.id}
                onClick={() => setFilterType(type.id)}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg transition ${
                  active
                    ? `${brandGradient} text-white shadow-sm`
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {type.label}{" "}
                <span
                  className={`ml-0.5 ${
                    active ? "opacity-80" : "text-slate-400"
                  }`}
                >
                  ({type.count})
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {filteredFiles.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center mx-auto mb-2">
              <FolderOpen size={20} className="text-slate-400" />
            </div>
            <p className="text-xs font-semibold text-slate-400">
              {filterType === "all"
                ? "No files shared yet"
                : `No ${filterType}s shared yet`}
            </p>
          </div>
        ) : (
          filteredFiles.map((file) => (
            <div
              key={`${file.messageId}-${file.fileName}`}
              className="bg-white rounded-xl border border-slate-200/60 shadow-[0px_4px_12px_rgba(25,28,29,0.03)] hover:shadow-[0px_8px_20px_rgba(25,28,29,0.06)] hover:border-[#00AAF1]/30 transition-all p-3"
            >

              <div className="flex items-center gap-2.5 mb-2.5">
                <div className="w-9 h-9 rounded-lg bg-slate-50 ring-1 ring-slate-200 grid place-items-center shrink-0">
                  {getFileIcon(file.fileName)}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-xs font-bold text-slate-800 truncate"
                    title={file.fileName}
                  >
                    {file.fileName}
                  </p>
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider mt-0.5">
                    <span>{(file.fileSize / 1024).toFixed(1)} KB</span>
                    <span className="text-slate-300">·</span>
                    <span className="text-[#03488A] normal-case tracking-normal">
                      {file.senderName}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-[10px] text-slate-400 mb-2.5">
                {formatMessageTime(file.timestamp)}
              </p>

              <div className="flex gap-1.5">
                <a
                  href={file.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-[#00AAF1]/40 hover:text-[#03488A] transition"
                >
                  <ExternalLink size={11} />
                  View
                </a>
                <button
                  onClick={() => handleViewFile(file.fileUrl, file.fileName)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${brandGradient} text-white shadow-sm hover:opacity-95 transition`}
                >
                  <Download size={11} />
                  Download
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-200/60 bg-slate-50/50">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
            {files.length > 0
              ? `${files.length} total · synced`
              : "No files yet"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FileHistorySidebar;
